import { ReactElement, useEffect } from 'react';

import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';
import { VisitAndAppointmentStatus, VisitEncounterMode } from '@dreryk/egabinet-visit-frontend-api';
import { PatientPresence } from '@dreryk/egabinet-appointment-frontend-api';
import { PatientGroup } from '@dreryk/egabinet-patient-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { FilterPanelLegacy, StyledFlex } from '@dreryk/react-components';
import { Range } from '@dreryk/watermelon';

import { DateFilterType } from '../AppointmentsFilterChips/DateChipFilter.types';
import { useAppointmentsFiltersContext } from '../AppointmentFilter.context';

import { ButtonGroupField } from './fields/ButtonGroupField/ButtonGroupField';
import { DoctorsFilter } from './fields/DoctorsFilter/DoctorsFilter';
import {
   DOCTORS_FILTER_FIELD_NAME,
   useDoctorsFilterSearchParamHandler,
} from './fields/DoctorsFilter/DoctorsFilter.hooks';
import { DateField } from './fields/DateField/DateField';
import { createDateFilter } from './AppointmentsFilterPanel.utils';
import { VisitAndAppointmentStatusField } from './fields/VisitAndAppointmentStatusField/VisitAndAppointmentStatusField';
import {
   doctorsFilterSessionStorage,
   useSyncPractitionerIdsFilterParamWithStorage,
} from './fields/DoctorsFilter/DoctorsFilter.storage';
import { VisitEncounterModeField } from './fields/VisitEncounterModeField/VisitEncounterModeField';
import { AppointmentConfirmationStatus } from './fields/AppointmentConfirmationStatusField/AppointmentConfirmationStatusField.types';
import { AppointmentConfirmationStatusField } from './fields/AppointmentConfirmationStatusField/AppointmentConfirmationStatusField';
import { PatientPresenceField } from './fields/PatientPresenceField/PatientPresenceField';
import { useSyncVisitAndAppointmentStatusFilterParamWithStorage } from './fields/VisitAndAppointmentStatusField/VisitAndAppointmentStatusField.storage';
import { useSyncVisitEncounterModeFilterParamWithStorage } from './fields/VisitEncounterModeField/VisitEncounterModeField.storage';
import { useSyncAppointmentConfirmationStatusFilterParamWithStorage } from './fields/AppointmentConfirmationStatusField/AppointmentConfirmationStatusField.storage';
import { useSyncPatientPresenceFilterParamWithStorage } from './fields/PatientPresenceField/PatientPresenceField.storage';
import { PatientGroupField } from './fields/PatientGroupField/PatientGroupField';
import { useSyncPatientGroupIdsFilterParamWithStorage } from './fields/PatientGroupField/PatientGroupField.storage';

export type FilterFormBean = {
   date: {
      type: DateFilterType | null;
      day?: Date;
      range?: Range<Date>;
   };
   doctors: Array<DoctorSimple>;
   visitAndAppointmentStatuses: VisitAndAppointmentStatus[];
   visitEncounterModes: VisitEncounterMode[];
   appointmentConfirmationStatuses: AppointmentConfirmationStatus[];
   patientPresences: PatientPresence[];
   patientGroups: PatientGroup[];
};

type AppointmentsFilterPanelProps = {
   readonly currentDoctor: DoctorSimple | null;
   readonly doctors: DoctorSimple[];
   readonly doctorsFilterAvailable: boolean;
   readonly patientGroups: PatientGroup[];
   readonly extendedFiltersAvailable?: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const AppointmentsFilterPanel = (props: AppointmentsFilterPanelProps): ReactElement => {
   const { getFilter, getSearchParams, replaceSearchParams } = useAppointmentsFiltersContext();

   useSyncPractitionerIdsFilterParamWithStorage(getSearchParams(), replaceSearchParams);
   useSyncVisitAndAppointmentStatusFilterParamWithStorage(getSearchParams(), replaceSearchParams);
   useSyncVisitEncounterModeFilterParamWithStorage(getSearchParams(), replaceSearchParams);
   useSyncAppointmentConfirmationStatusFilterParamWithStorage(getSearchParams(), replaceSearchParams);
   useSyncPatientPresenceFilterParamWithStorage(getSearchParams(), replaceSearchParams);
   useSyncPatientGroupIdsFilterParamWithStorage(getSearchParams(), replaceSearchParams);

   const dateFilter = getFilter('date');
   const practitionerIds = getFilter('practitioner');
   const visitAndAppointmentStatuses = getFilter('visitAndAppointmentStatus');
   const visitEncounterModes = getFilter('visitEncounterMode');
   const appointmentConfirmationStatuses = getFilter('appointmentConfirmationStatus');
   const patientPresences = getFilter('patientPresence');
   const patientGroupIds = getFilter('patientGroup');

   const { register, control, watch, reset, setValue } = useFormController<FilterFormBean>({
      date: { type: null },
      doctors: [],
      visitAndAppointmentStatuses: [],
      visitEncounterModes: [],
      appointmentConfirmationStatuses: [],
      patientPresences: [],
      patientGroups: [],
   });

   const updateDoctorsSearchParam = useDoctorsFilterSearchParamHandler(props.doctors);

   useEffect(() => {
      if (props.currentDoctor === null) {
         if (props.doctors.length > 1) {
            if (!practitionerIds.length && !doctorsFilterSessionStorage.getStoredPractitionerIds().length) {
               updateDoctorsSearchParam();
               doctorsFilterSessionStorage.setPractitionerIdsFilter(
                  props.doctors.map((doctor) => PractitionerId.of(doctor.practitionerId))
               );
            }
            reset({
               doctors: practitionerIds
                  ? props.doctors.filter((doctor) =>
                       practitionerIds.some((id) => id.asNumber() === doctor.practitionerId)
                    )
                  : [],
            });
         }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.doctors, props.currentDoctor]);

   useEffect(() => {
      let doctors: DoctorSimple[] = [];
      if (practitionerIds.length) {
         doctors = props.doctors.filter((doctor) =>
            practitionerIds.some((id) => id.asNumber() === doctor.practitionerId)
         );
      }

      let patientGroups: PatientGroup[] = [];
      if (patientGroupIds.length) {
         patientGroups = props.patientGroups.filter(({ id }) =>
            patientGroupIds.some((patientGroupId) => Number(patientGroupId) === id)
         );
      }

      const formValues: FilterFormBean = {
         date: createDateFilter(dateFilter),
         doctors: doctors,
         visitAndAppointmentStatuses: visitAndAppointmentStatuses,
         visitEncounterModes: visitEncounterModes,
         appointmentConfirmationStatuses: appointmentConfirmationStatuses,
         patientPresences: patientPresences,
         patientGroups: patientGroups,
      };

      reset(formValues);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [
      dateFilter,
      practitionerIds,
      visitAndAppointmentStatuses,
      visitEncounterModes,
      appointmentConfirmationStatuses,
      patientPresences,
      patientGroupIds,
   ]);

   return (
      <FilterPanelLegacy testId={'appointmentsFilter'}>
         <StyledFlex gap={12} flexWrap={'wrap'}>
            <ButtonGroupField register={register} />
            <DateField control={control} register={register} currentFilter={dateFilter} />
            {props.doctorsFilterAvailable ? (
               <DoctorsFilter
                  register={register}
                  currentFilters={watch(DOCTORS_FILTER_FIELD_NAME)}
                  setValue={setValue}
                  doctors={props.doctors}
               />
            ) : null}
            <VisitAndAppointmentStatusField register={register} currentFilter={watch('visitAndAppointmentStatuses')} />
            {props.extendedFiltersAvailable ?? true ? (
               <>
                  <VisitEncounterModeField register={register} currentFilter={watch('visitEncounterModes')} />
                  <AppointmentConfirmationStatusField
                     register={register}
                     currentFilter={watch('appointmentConfirmationStatuses')}
                  />
                  <PatientPresenceField register={register} currentFilters={watch('patientPresences')} />
               </>
            ) : null}
            <PatientGroupField
               register={register}
               currentFilter={watch('patientGroups')}
               patientGroups={props.patientGroups}
            />
         </StyledFlex>
      </FilterPanelLegacy>
   );
};
